import React, { useState, useEffect, useRef, useCallback } from 'react';
import { VoiceAgentRecorder } from '@/components/voice/VoiceAgentRecorder';
import agronomistAvatar from '@/assets/agronomist-avatar.png';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Screen } from '@/components/layout/Screen';
import { useNetworkMonitor } from '@/features/network';
import { agriAgentService, AgentResponse } from '@/features/agriagent';
import {
  Camera,
  Mic,
  Volume2,
  BookOpen,
  Play,
  ThumbsUp,
  ThumbsDown,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { edgeFunctions } from '@/services/edgeFunctions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthenticatedClient, getCurrentUserId } from '@/services/client';
import { supabase } from '@/integrations/supabase/client';
import { getSignedAudioUrl, SIGNED_AUDIO_TTL_SECONDS } from '@/features/audio/signedAudio';
import { format } from 'date-fns';

// Simple Mode hook with fallback
let useSimpleMode: () => { isSimpleMode: boolean };
try {
  useSimpleMode = require("@/contexts/SimpleModeContext").useSimpleMode;
} catch {
  useSimpleMode = () => ({ isSimpleMode: false });
}

interface Message {
  id: string;
  sender: 'user' | 'agent';
  content: string;
  timestamp: Date;
  isVoice?: boolean;
  audioUrl?: string;
}

interface VoiceConversation {
  id: string;
  user_transcription: string | null;
  response_text: string | null;
  user_audio_url: string;
  response_audio_url: string | null;
  language: string;
  status: string | null;
  created_at: string | null;
  user_audio_duration_seconds: number | null;
  response_audio_duration_seconds: number | null;
  was_helpful: boolean | null;
}

export const AgriAgentScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isOnline } = useNetworkMonitor();
  const { isSimpleMode } = useSimpleMode();
  const queryClient = useQueryClient();
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showTextInput, setShowTextInput] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('agent');
  const [expandedQuestion, setExpandedQuestion] = useState(false);
  const [expandedAnswer, setExpandedAnswer] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);

  const currentUserId = getCurrentUserId();

  // Set userId on the agent service so it gets passed to the edge function
  useEffect(() => {
    if (currentUserId) {
      agriAgentService.setUserId(currentUserId);
    }
  }, [currentUserId]);

  // Fetch latest voice conversation from Supabase
  const { data: latestConversation } = useQuery({
    queryKey: ['voice_conversations_latest', currentUserId],
    queryFn: async () => {
      if (!currentUserId) return null;
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from('voice_conversations')
        .select('id, user_transcription, response_text, user_audio_url, response_audio_url, language, status, created_at, user_audio_duration_seconds, response_audio_duration_seconds, was_helpful')
        .eq('user_id', currentUserId)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        console.error('Failed to fetch latest conversation:', error);
        return null;
      }
      return data as VoiceConversation | null;
    },
    enabled: !!currentUserId,
    staleTime: 1000 * 60 * 5,
  });

  // Bug 1: rehydrate live chat from latestConversation when messages array is
  // empty (i.e. on screen mount or after navigating away and back). Without
  // this, the chat-bubble view is lost on every unmount because messages is
  // local React state. Only fires when the query resolves with a completed
  // row that has both transcript and response.
  useEffect(() => {
    if (messages.length > 0) return;
    if (!latestConversation || latestConversation.status !== 'completed') return;
    if (!latestConversation.user_transcription || !latestConversation.response_text) return;

    const userMessage: Message = {
      id: `user-${latestConversation.id}`,
      sender: 'user',
      content: latestConversation.user_transcription,
      timestamp: latestConversation.created_at ? new Date(latestConversation.created_at) : new Date(),
      isVoice: true,
      audioUrl: latestConversation.user_audio_url || undefined,
    };

    const agentMessage: Message = {
      id: `agent-${latestConversation.id}`,
      sender: 'agent',
      content: latestConversation.response_text,
      timestamp: latestConversation.created_at ? new Date(latestConversation.created_at) : new Date(),
      audioUrl: latestConversation.response_audio_url ?? undefined,
    };

    setMessages([userMessage, agentMessage]);
  }, [latestConversation, messages.length]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle incoming question from knowledge base
  useEffect(() => {
    const question = searchParams.get('q');
    if (question) {
      handleSendMessage(question);
      window.history.replaceState({}, '', '/agent');
    }
  }, [searchParams]);

  // Generate TTS audio blob URL from text via server-side Google Cloud TTS
  const generateTtsAudio = async (text: string): Promise<string | null> => {
    try {
      const { data, error } = await edgeFunctions.textToSpeech(text, i18n.language || 'bn');
      if (error || !data) return null;
      // voice-tts returns { success, audio_base64, content_type } — not audioUrl
      const ttsData = data as unknown as { success?: boolean; audio_base64?: string; content_type?: string };
      if (!ttsData.success || !ttsData.audio_base64) return null;
      const binaryStr = atob(ttsData.audio_base64);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
      const blob = new Blob([bytes], { type: ttsData.content_type || 'audio/mp3' });
      return URL.createObjectURL(blob);
    } catch (err) {
      console.warn('TTS generation failed:', err);
      return null;
    }
  };

  // Play audio from URL, with state tracking
  const playAudioUrl = async (url: string, messageId: string) => {
    // Stop any currently playing audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    window.speechSynthesis?.cancel();

    // voice-messages is a PRIVATE bucket (FIX-CCS-AUDIO-PRIVACY): a stored value that is a bare
    // PATH (not an http URL) is a private voice-messages object and must be signed on read.
    // Full http URLs (response_audio_url / error audio in the public voice-responses bucket) play as-is.
    let src = url;
    if (url && !/^https?:\/\//i.test(url)) {
      const signed = await getSignedAudioUrl(url, 'voice-messages');
      if (!signed) { setPlayingMessageId(null); return; }
      src = signed;
    }

    const audio = new Audio(src);
    currentAudioRef.current = audio;
    setPlayingMessageId(messageId);
    audio.onended = () => {
      setPlayingMessageId(null);
      currentAudioRef.current = null;
    };
    audio.onerror = () => {
      setPlayingMessageId(null);
      currentAudioRef.current = null;
    };
    audio.play().catch(err => {
      console.warn('Audio auto-play blocked:', err);
      setPlayingMessageId(null);
      currentAudioRef.current = null;
    });
  };

  const handleSendMessage = async (content: string, isVoice = false) => {
    if (!content.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      sender: 'user',
      content,
      timestamp: new Date(),
      isVoice,
    };

    setMessages(prev => [...prev, userMessage]);
    setIsProcessing(true);
    setTextInput('');
    setShowTextInput(false);

    try {
      const response: AgentResponse = await agriAgentService.sendMessage(content, i18n.language);
      const fullText = response.content;

      // Generate TTS audio for the full response (non-blocking start)
      const ttsPromise = generateTtsAudio(fullText);

      // Add response chunks to chat with staggered timing
      const responseChunks = splitIntoChunks(fullText, 2000);
      const lastMessageId = `${Date.now()}-${responseChunks.length - 1}`;

      responseChunks.forEach((chunk, index) => {
        setTimeout(() => {
          const msgId = `${Date.now()}-${index}`;
          const agentMessage: Message = {
            id: index === responseChunks.length - 1 ? lastMessageId : msgId,
            sender: 'agent',
            content: chunk,
            timestamp: new Date(),
          };
          setMessages(prev => [...prev, agentMessage]);

          if (index === responseChunks.length - 1) {
            setIsProcessing(false);
          }
        }, index * 500);
      });

      if ('vibrate' in navigator) {
        navigator.vibrate([10, 50, 10]);
      }

      // Wait for TTS to complete, then auto-play and store URL on last message
      const ttsUrl = await ttsPromise;
      if (ttsUrl) {
        // Store the audio URL on the last agent message for replay
        setMessages(prev => prev.map(m =>
          m.id === lastMessageId ? { ...m, audioUrl: ttsUrl } : m
        ));
        // Auto-play after a short delay to let the last chunk render
        const playDelay = responseChunks.length * 500 + 200;
        setTimeout(() => playAudioUrl(ttsUrl, lastMessageId), playDelay);
      } else {
        // Server TTS failed — fall back to browser speechSynthesis
        const playDelay = responseChunks.length * 500 + 200;
        setTimeout(() => {
          if ('speechSynthesis' in window) {
            setPlayingMessageId(lastMessageId);
            const utterance = new SpeechSynthesisUtterance(fullText);
            utterance.lang = i18n.language || 'bn';
            utterance.rate = 0.9;
            utterance.onend = () => setPlayingMessageId(null);
            window.speechSynthesis.speak(utterance);
          }
        }, playDelay);
      }
    } catch (error) {
      setIsProcessing(false);
      toast.error(t('agent.error', 'Failed to get response'));
    }
  };

  const splitIntoChunks = (text: string, maxLength: number): string[] => {
    if (text.length <= maxLength) return [text];
    
    const sentences = text.match(/[^.!?।]+[.!?।]+/g) || [text];
    const chunks: string[] = [];
    let currentChunk = '';

    sentences.forEach(sentence => {
      if ((currentChunk + sentence).length <= maxLength) {
        currentChunk += sentence;
      } else {
        if (currentChunk) chunks.push(currentChunk.trim());
        currentChunk = sentence;
      }
    });

    if (currentChunk) chunks.push(currentChunk.trim());
    return chunks.length > 0 ? chunks : [text];
  };

  // Voice path: upload to storage → insert voice_conversations row → call
  // voice-orchestrator → append result to local chat. Error-code path plays
  // the pre-recorded Lao audio clip returned by the orchestrator.
  const handleSendVoice = useCallback(async (audioBlob: Blob, duration: number) => {
    // Flip uploading flag at the very top so the recorder's true→false reset
    // effect always fires in finally, regardless of which branch returns.
    setIsUploading(true);
    setIsProcessing(true);

    try {
      if (!audioBlob || audioBlob.size === 0) {
        toast.error(t('agent.recordingTooShort', 'Recording too short, please try again'));
        return;
      }

      if (!currentUserId) {
        toast.error(t('agent.pleaseLogin', 'Please sign in to use voice'));
        return;
      }

      if (!isOnline) {
        toast.error(t('agent.offlineVoiceNotSupported', "You're offline. Please try again when back online."));
        return;
      }

      // 1. Upload audio to voice-messages bucket
      const mimeType = audioBlob.type || 'audio/webm';
      const extension = mimeType.includes('mp4') ? 'm4a'
        : mimeType.includes('ogg') ? 'ogg'
        : 'webm';
      const filename = `${currentUserId}/${Date.now()}.${extension}`;

      const client = getAuthenticatedClient();
      const { error: uploadError } = await client.storage
        .from('voice-messages')
        .upload(filename, audioBlob, { contentType: mimeType, upsert: false });

      if (uploadError) {
        toast.error(t('agent.uploadFailed', 'Upload failed') + ': ' + uploadError.message);
        return;
      }

      // voice-messages is a PRIVATE bucket (FIX-CCS-AUDIO-PRIVACY): sign the object so the
      // orchestrator can fetch it, and store the PATH for later playback signing.
      const audioPath = filename;
      const { data: signedForOrch } = await client.storage.from('voice-messages').createSignedUrl(filename, SIGNED_AUDIO_TTL_SECONDS);
      const audioUrl = signedForOrch?.signedUrl;
      if (!audioUrl) {
        toast.error(t('agent.uploadFailed', 'Upload failed') + ': could not prepare audio');
        return;
      }

      // 2. Insert voice_conversations row (RLS: x-session-token via authenticated client)
      const language = i18n.language;
      const { data: convData, error: convError } = await client
        .from('voice_conversations')
        .insert({
          user_id: currentUserId,
          user_audio_url: audioPath,
          user_audio_duration_seconds: duration,
          language,
          status: 'pending',
        })
        .select()
        .single();

      if (convError || !convData) {
        toast.error(t('agent.saveFailed', 'Save failed') + ': ' + (convError?.message ?? 'unknown'));
        return;
      }

      // 3. Call voice-orchestrator
      const { data: orchestratorData, error: orchestratorError } = await edgeFunctions.voiceOrchestrate(
        convData.id,
        audioUrl,
        language,
        currentUserId,
      );

      if (orchestratorError) {
        toast.error(t('agent.error', 'Failed to get response') + ': ' + orchestratorError.message);
        return;
      }

      const result = orchestratorData;

      // 4. Confidence-gate path: orchestrator returns error_code + Lao audio URL
      if (result?.error_code) {
        if (result.error_audio_url) {
          const errAudio = new Audio(result.error_audio_url);
          errAudio.play().catch(e => console.warn('Error audio blocked:', e));
        }

        if (result.error_code === 'NO_SPEECH') {
          toast.error(t('agent.noSpeechTitle', "I didn't hear anything"));
        } else if (result.error_code === 'LOW_CONFIDENCE') {
          toast.error(t('agent.lowConfidenceTitle', "I couldn't understand"));
        } else if (result.error_code === 'QUOTA_EXCEEDED') {
          toast.error(t('agent.quotaExceeded', 'Daily voice limit reached'));
        }
        return;
      }

      // 5. Success path: append user transcript + agent response to live chat
      if (!result?.success || !result.transcription?.text || !result.response?.text) {
        toast.error(t('agent.error', 'Failed to get response'));
        return;
      }

      const transcriptText = result.transcription.text;
      const responseText = result.response.text;
      const responseAudioUrl = result.tts?.audio_url ?? null;

      const userMessage: Message = {
        id: `user-${Date.now()}`,
        sender: 'user',
        content: transcriptText,
        timestamp: new Date(),
        isVoice: true,
        audioUrl,
      };

      const agentMessage: Message = {
        id: `agent-${Date.now()}`,
        sender: 'agent',
        content: responseText,
        timestamp: new Date(),
        audioUrl: responseAudioUrl ?? undefined,
      };

      setMessages(prev => [...prev, userMessage, agentMessage]);

      // Refresh the React Query caches that drive the latestConversation card
      // and the PastQuestionsScreen list so the new row is visible without a
      // 5-min staleTime wait.
      queryClient.invalidateQueries({ queryKey: ['voice_conversations_latest', currentUserId] });
      queryClient.invalidateQueries({ queryKey: ['voice_conversations_all', currentUserId] });

      if ('vibrate' in navigator) navigator.vibrate([10, 50, 10]);

      // Auto-play the orchestrator-returned TTS audio (if available)
      if (responseAudioUrl) {
        setTimeout(() => playAudioUrl(responseAudioUrl, agentMessage.id), 200);
      }
    } catch (err) {
      console.error('Voice send error:', err);
      toast.error(t('common.somethingWentWrong', 'Something went wrong'));
    } finally {
      setIsUploading(false);
      setIsProcessing(false);
    }
  }, [currentUserId, isOnline, i18n.language, t, queryClient]);

  // Realtime subscription on voice_conversations updates — primarily for the
  // latestConversation card in the empty state, so the next render reflects
  // the orchestrator's writes without a manual refetch.
  useEffect(() => {
    if (!currentUserId) return;

    const channel = supabase
      .channel(`voice_updates_${currentUserId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'voice_conversations',
        filter: `user_id=eq.${currentUserId}`,
      }, () => {
        // Invalidate both voice query caches on any UPDATE/INSERT/DELETE so
        // external changes (orchestrator writes, demo cleanup, other tabs)
        // propagate to the latestConversation card and PastQuestionsScreen.
        queryClient.invalidateQueries({ queryKey: ['voice_conversations_latest', currentUserId] });
        queryClient.invalidateQueries({ queryKey: ['voice_conversations_all', currentUserId] });
      })
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [currentUserId, queryClient]);

  // Cleanup any in-flight audio playback on unmount
  useEffect(() => {
    return () => {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
    };
  }, []);

  const handleTapToHear = async (messageId: string, content: string) => {
    if ('vibrate' in navigator) navigator.vibrate(10);

    // Toggle off if already playing this message
    if (playingMessageId === messageId) {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      window.speechSynthesis?.cancel();
      setPlayingMessageId(null);
      return;
    }

    // 1. Check if message has a stored audio URL (from prior TTS generation)
    const msg = messages.find(m => m.id === messageId);
    if (msg?.audioUrl) {
      playAudioUrl(msg.audioUrl, messageId);
      return;
    }

    // 2. Generate server-side TTS on demand
    setPlayingMessageId(messageId);
    const ttsUrl = await generateTtsAudio(content);
    if (ttsUrl) {
      // Store for future replays
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, audioUrl: ttsUrl } : m
      ));
      playAudioUrl(ttsUrl, messageId);
      return;
    }

    // 3. Last fallback: browser speechSynthesis
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(content);
      utterance.lang = i18n.language || 'bn';
      utterance.rate = 0.9;
      utterance.onend = () => setPlayingMessageId(null);
      window.speechSynthesis.speak(utterance);
    } else {
      setPlayingMessageId(null);
    }
  };

  const handleTextSubmit = () => {
    if (textInput.trim()) {
      handleSendMessage(textInput.trim());
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFeedback = async (conversationId: string, helpful: boolean) => {
    try {
      const client = getAuthenticatedClient();
      await client
        .from('voice_conversations')
        .update({ was_helpful: helpful, feedback_at: new Date().toISOString() })
        .eq('id', conversationId);
      toast.success(helpful ? `👍 ${t("agent.feedbackThanks", { defaultValue: "Thanks for your feedback!" })}` : `👎 ${t("agent.feedbackThanksImprove", { defaultValue: "Thanks, we'll improve!" })}`);
    } catch {
      toast.error(t("agent.feedbackFailed", { defaultValue: "Failed to save feedback" }));
    }
  };

  const hasLiveMessages = messages.length > 0;

  return (
    <Screen
      showTopBar={true}
      topBarProps={{ 
        title: t('agent.title', { defaultValue: 'AgriAgent Chat' }),
        showMapPin: false,
        showSettings: false,
        rightAction: (
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/knowledge')}
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 active:scale-95 transition-all"
              aria-label={t("knowledge.title")}
            >
              <BookOpen className="w-5 h-5 text-white" />
            </button>
            <span className="flex items-center gap-1 text-xs text-white/80">
              <span className={cn("w-2 h-2 rounded-full", isOnline ? "bg-success" : "bg-warning")} />
              {isOnline ? t("network.online") : t("network.offline")}
            </span>
          </div>
        ),
      }}
      showBottomNav={true}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      className="flex flex-col bg-background"
    >
      {/* MIDDLE — Content Area */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {hasLiveMessages ? (
          /* Live Messages from current session */
          <div className="space-y-3">
            {messages.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={cn(
                  "flex",
                  msg.sender === 'user' ? "justify-end" : "justify-start"
                )}
              >
                {msg.sender === 'agent' && (
                  <div className="w-8 h-8 rounded-full overflow-hidden mr-2 flex-shrink-0">
                    <img src={agronomistAvatar} alt="AgriAgent" className="w-full h-full object-cover" />
                  </div>
                )}

                <div className={cn(
                  "max-w-[75%] rounded-2xl px-4 py-3",
                  msg.sender === 'user'
                    ? "bg-primary text-primary-foreground rounded-br-sm"
                    : "bg-muted text-foreground rounded-bl-sm"
                )}>
                  {msg.sender === 'user' ? (
                    msg.audioUrl ? (
                      <button
                        onClick={() => handleTapToHear(msg.id, msg.content)}
                        className="flex items-center gap-2"
                        aria-label={t('agent.replayQuestion', { defaultValue: 'Replay your question' })}
                      >
                        {playingMessageId === msg.id ? (
                          <Volume2 className="w-5 h-5 animate-pulse" />
                        ) : (
                          <Mic className="w-5 h-5 opacity-70" />
                        )}
                        <span className="text-xs opacity-80">
                          {playingMessageId === msg.id
                            ? t('agent.playing', { defaultValue: 'Playing...' })
                            : t('agent.tapToReplay', { defaultValue: 'Tap to replay' })}
                        </span>
                      </button>
                    ) : (
                      <Mic className="w-5 h-5 opacity-70" />
                    )
                  ) : (
                    <button
                      onClick={() => handleTapToHear(msg.id, msg.content)}
                      className="flex items-center gap-2"
                    >
                      <Volume2 className={cn(
                        "w-5 h-5",
                        playingMessageId === msg.id ? "text-primary animate-pulse" : "text-muted-foreground"
                      )} />
                      <span className="text-xs text-muted-foreground">
                        {playingMessageId === msg.id ? t("agent.playing", { defaultValue: "Playing..." }) : t("agent.tapToHear", { defaultValue: "Tap to hear" })}
                      </span>
                    </button>
                  )}
                </div>
              </motion.div>
            ))}

            {/* Typing indicator */}
            {isProcessing && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-2"
              >
                <div className="w-8 h-8 rounded-full overflow-hidden">
                  <img src={agronomistAvatar} alt="AgriAgent" className="w-full h-full object-cover" />
                </div>
                <div className="bg-muted rounded-2xl px-4 py-3 rounded-bl-sm">
                  <div className="flex gap-1">
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.2 }}
                        className="w-2 h-2 rounded-full bg-muted-foreground/50"
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            <div ref={messagesEndRef} />
          </div>
        ) : (
          /* Empty state with agent identity + latest conversation card */
          <div className="flex flex-col items-center h-full pt-2">
            {/* Agent identity — shrunk 50% so mic + send/cancel buttons fit without scrolling */}
            <div className="relative mb-2">
              <div className="w-10 h-10 rounded-full overflow-hidden">
                <img src={agronomistAvatar} alt="AgriAgent" className="w-full h-full object-cover" />
              </div>
              <div className={cn(
                "absolute bottom-0 right-0 w-2 h-2 rounded-full border-2 border-background",
                isOnline ? "bg-success" : "bg-warning"
              )} />
            </div>
            <h2 className="text-xl font-semibold text-foreground">{t("agent.name", { defaultValue: "AgriAgent" })}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t("agent.subtitle", { defaultValue: "Your AI farming advisor" })}</p>

            {/* Latest conversation card from Supabase */}
            {latestConversation ? (
              <div className="w-full mt-6 space-y-3">
                {/* Conversation Card */}
                <div className="border border-border rounded-2xl bg-card overflow-hidden">
                  {/* Header row: date + status */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
                    <span className="text-sm text-muted-foreground">
                      {latestConversation.created_at
                        ? format(new Date(latestConversation.created_at), 'dd/MM/yyyy')
                        : '—'}
                    </span>
                    <span className="text-xs font-medium text-success bg-success/10 px-2 py-0.5 rounded-full">
                      {t("agent.ready", { defaultValue: "Ready!" })}
                    </span>
                  </div>

                  {/* Your question */}
                  <div className="px-4 py-3 border-b border-border/50">
                    <p className="text-xs font-medium text-muted-foreground mb-2">{t("agent.yourQuestion", { defaultValue: "Your question" })}</p>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleTapToHear('user-latest', latestConversation.user_transcription || '')}
                        className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0"
                      >
                        <Play className="w-5 h-5 text-success ml-0.5" />
                      </button>
                      <div className="flex-1">
                        {/* Waveform placeholder */}
                        <div className="flex items-center gap-0.5 h-6">
                          {[...Array(24)].map((_, i) => (
                            <div
                              key={i}
                              className="w-1 bg-success/40 rounded-full"
                              style={{ height: `${Math.sin(i * 0.5) * 12 + 10}px` }}
                            />
                          ))}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatDuration(latestConversation.user_audio_duration_seconds)}
                        </span>
                      </div>
                    </div>
                    {/* Transcription text hidden — voice-only UI */}
                  </div>

                  {/* AgriAgent answer */}
                  <div className="px-4 py-3 border-b border-border/50">
                    <p className="text-xs font-medium text-muted-foreground mb-2">{t("agent.agentAnswer", { defaultValue: "AgriAgent answer" })}</p>
                    <button
                      onClick={() => {
                        if (latestConversation.response_text) {
                          handleTapToHear('agent-latest', latestConversation.response_text);
                        }
                      }}
                      className="flex items-center gap-3 w-full text-left"
                    >
                      <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0">
                        <Volume2 className="w-5 h-5 text-success" />
                      </div>
                      <span className="text-sm text-success font-medium">
                        {playingMessageId === 'agent-latest' ? t("agent.playing", { defaultValue: "Playing..." }) : t("agent.tapToListen", { defaultValue: "Tap to listen" })}
                      </span>
                    </button>
                    {/* Response text hidden — voice-only UI */}
                  </div>

                  {/* Was this helpful? */}
                  <div className="px-4 py-3 flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t("agent.wasThisHelpful", { defaultValue: "Was this helpful?" })}</span>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleFeedback(latestConversation.id, true)}
                        className={cn(
                          "w-9 h-9 rounded-full flex items-center justify-center transition-colors",
                          latestConversation.was_helpful === true
                            ? "bg-success/20 text-success"
                            : "bg-muted text-muted-foreground hover:bg-success/10"
                        )}
                      >
                        <ThumbsUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleFeedback(latestConversation.id, false)}
                        className={cn(
                          "w-9 h-9 rounded-full flex items-center justify-center transition-colors",
                          latestConversation.was_helpful === false
                            ? "bg-destructive/20 text-destructive"
                            : "bg-muted text-muted-foreground hover:bg-destructive/10"
                        )}
                      >
                        <ThumbsDown className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Past Questions button */}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/agent/history')}
                >
                  📋 {t("agent.pastQuestions", { defaultValue: "Past Questions" })}
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </Button>
              </div>
            ) : (
              /* No conversations yet */
              <div className="flex flex-col items-center mt-8">
                <div className="flex gap-2 mt-4">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.3 }}
                      className="w-3 h-3 rounded-full bg-primary"
                    />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mt-6">
                  {t("agent.noQuestions", { defaultValue: "No questions yet. Hold the mic to ask AgriAgent!" })}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* BOTTOM — Voice-First Input Area (orchestrator-backed) */}
      <div className="bg-card border-t border-border px-4 py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
        {/* Camera button */}
        <button
          onClick={() => navigate('/camera')}
          className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-2 mx-auto active:scale-95 transition-all"
          aria-label={t('camera.title', 'Camera')}
        >
          <Camera className="w-6 h-6 text-muted-foreground" />
        </button>

        {/* CCS-pattern recorder: hold-to-speak → preview → send. Calls
            handleSendVoice which uploads + inserts + orchestrates. */}
        <VoiceAgentRecorder
          onSend={handleSendVoice}
          onCancel={() => { /* user cancelled before send — no state to clear */ }}
          questionsRemaining={99}
          disabled={!isOnline || isUploading || isProcessing}
          isUploading={isUploading}
        />
      </div>
    </Screen>
  );
};
